import { defineComponent as _defineComponent } from 'vue'
import { storeToRefs } from 'pinia'
import { getCurrentInstance, watch } from 'vue'

import { useInstagramScenarioEditorStore } from '@/client/features/instagram_instantwin_prize/hooks/instagramScenarioEditorStore'
import { TrackingService } from '@/client/services'


export default /*#__PURE__*/_defineComponent({
  __name: 'StepResult',
  setup(__props) {

// @ts-nocheck TODO: 既存の型エラーを解消したら外す
const { proxy } = getCurrentInstance() as any

// Store
const store = useInstagramScenarioEditorStore()
const { result, errors } = storeToRefs(store)

const getFileExtension = (name: string): string => {
  const index = name.lastIndexOf('.')
  return index >= 0 ? name.slice(index).toLowerCase() : ''
}

const isSupportedImageExtension = (name: string): boolean => {
  return ['.jpg', '.jpeg', '.png'].includes(getFileExtension(name))
}

/**
 * 当選メディアが画像かどうかを判定
 */
const isWinImageFile = (): boolean => {
  if (!result.value.winUrl) {
    return false
  }
  // fileTypeプロパティがあれば優先的に使用（blob URLの場合）
  if (result.value.winFileType) {
    return result.value.winFileType === 'image'
  }
  // fileTypeがない場合はURLの拡張子で判定（サーバーからのURLの場合）
  const imageExtensions = ['.png', '.jpg', '.jpeg']
  const url = result.value.winUrl.toLowerCase()
  return imageExtensions.some(ext => url.includes(ext)) || result.value.winUrl.startsWith('data:image/')
}

/**
 * 落選メディアが画像かどうかを判定
 */
const isLoseImageFile = (): boolean => {
  if (!result.value.loseUrl) {
    return false
  }
  // fileTypeプロパティがあれば優先的に使用（blob URLの場合）
  if (result.value.loseFileType) {
    return result.value.loseFileType === 'image'
  }
  // fileTypeがない場合はURLの拡張子で判定（サーバーからのURLの場合）
  const imageExtensions = ['.png', '.jpg', '.jpeg']
  const url = result.value.loseUrl.toLowerCase()
  return imageExtensions.some(ext => url.includes(ext)) || result.value.loseUrl.startsWith('data:image/')
}

/**
 * 当選メディアファイル名を取得
 */
const getWinMediaFileName = (): string => {
  // fileNameフィールドがあればそれを優先
  if (result.value.winFileName) {
    return result.value.winFileName
  }

  // blob URLの場合は拡張子から生成
  if (result.value.winUrl && result.value.winUrl.startsWith('blob:')) {
    const fileType = result.value.winFileType === 'image' ? 'png' : 'mp4'
    return `media.${fileType}`
  }
  // サーバーURLの場合はURLから抽出
  if (result.value.winUrl) {
    const urlWithoutQuery = result.value.winUrl.split('?')[0]
    const urlParts = urlWithoutQuery.split('/')
    const fileName = urlParts[urlParts.length - 1]

    return decodeURIComponent(fileName)
  }
  return '当選メディア'
}

/**
 * 落選メディアファイル名を取得
 */
const getLoseMediaFileName = (): string => {
  // fileNameフィールドがあればそれを優先
  if (result.value.loseFileName) {
    return result.value.loseFileName
  }

  // blob URLの場合は拡張子から生成
  if (result.value.loseUrl && result.value.loseUrl.startsWith('blob:')) {
    const fileType = result.value.loseFileType === 'image' ? 'png' : 'mp4'
    return `media.${fileType}`
  }
  // サーバーURLの場合はURLから抽出
  if (result.value.loseUrl) {
    const urlWithoutQuery = result.value.loseUrl.split('?')[0]
    const urlParts = urlWithoutQuery.split('/')
    const fileName = urlParts[urlParts.length - 1]

    return decodeURIComponent(fileName)
  }
  return '落選メディア'
}

/**
 * 当選メディア選択ボタンのクリックハンドラ
 */
const handleWinMediaSelectClick = () => {
  const fileInput = document.getElementById('winMediaFileInput') as HTMLInputElement
  if (fileInput) {
    fileInput.click()
  }
}

/**
 * 落選メディア選択ボタンのクリックハンドラ
 */
const handleLoseMediaSelectClick = () => {
  const fileInput = document.getElementById('loseMediaFileInput') as HTMLInputElement
  if (fileInput) {
    fileInput.click()
  }
}

/**
 * メディアファイル変更の共通処理
 */
const handleMediaFileChange = async (event: Event, mediaType: 'win' | 'lose') => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]

  if (!file) {
    return
  }

  // ファイル形式チェック
  const imageTypes = ['image/png', 'image/jpeg']
  const videoTypes = ['video/mp4']

  const isImage = imageTypes.includes(file.type)
  const isVideo = videoTypes.includes(file.type)

  if (!isImage && !isVideo) {
    proxy.$notify({
      title: '対応形式はpng、jpg、mp4です。',
      customClass: 'danger',
      duration: 5000
    })
    // ファイル入力をリセット
    target.value = ''
    return
  }

  if (isImage && !isSupportedImageExtension(file.name)) {
    proxy.$notify({
      title: '対応形式はpng、jpg、mp4です。',
      customClass: 'danger',
      duration: 5000
    })
    target.value = ''
    return
  }

  // ファイルサイズチェック（画像8MB未満、動画25MB未満）
  const maxSize = isImage ? 8 * 1024 * 1024 : 25 * 1024 * 1024
  const sizeLabel = isImage ? '8MB' : '25MB'
  const fileTypeLabel = isImage ? '画像' : '動画'

  if (file.size >= maxSize) {
    proxy.$notify({
      title: `${fileTypeLabel}ファイルのサイズは${sizeLabel}未満にしてください。`,
      customClass: 'danger',
      duration: 5000
    })
    // ファイル入力をリセット
    target.value = ''
    return
  }

  // 画像/動画ともにAPIアップロードを実行し、mediaId を保存する
  try {
    const uploadResult = await store.uploadMedia(file)
    const fileName = uploadResult.mediaOriginName || file.name
    const updates =
      mediaType === 'win'
        ? {
            winMediaId: uploadResult.mediaId,
            winUrl: uploadResult.url,
            winFileType: (isImage ? 'image' : 'video') as const,
            winFileName: fileName
          }
        : {
            loseMediaId: uploadResult.mediaId,
            loseUrl: uploadResult.url,
            loseFileType: (isImage ? 'image' : 'video') as const,
            loseFileName: fileName
          }
    store.updateResult(updates)
  } catch (error) {
    TrackingService.reportError(
      error,
      {
        feature: 'instagram_instantwin',
        action: 'upload_result_media',
        screen: 'InstagramScenarioEditorDialog.StepResult',
        prizeId: typeof store.prizeId === 'number' ? store.prizeId : undefined,
        node: `result_${mediaType}`,
        fileType: isImage ? 'image' : 'video',
        fileSize: file.size
      },
      { mode: 'always' }
    )
    proxy.$notify({
      title: 'メディアのアップロードに失敗しました',
      customClass: 'danger',
      duration: 5000
    })
  }

  // ファイル入力をリセット
  target.value = ''
}

/**
 * 当選メディアファイル変更ハンドラ
 */
const handleWinMediaFileChange = async (event: Event) => {
  await handleMediaFileChange(event, 'win')
}

/**
 * 落選メディアファイル変更ハンドラ
 */
const handleLoseMediaFileChange = async (event: Event) => {
  await handleMediaFileChange(event, 'lose')
}

/**
 * 当選メディア削除ハンドラ
 */
const handleWinMediaDelete = () => {
  // Blob URLの場合はメモリ解放
  if (result.value.winUrl && result.value.winUrl.startsWith('blob:')) {
    URL.revokeObjectURL(result.value.winUrl)
  }
  store.clearWinMedia()
}

/**
 * 落選メディア削除ハンドラ
 */
const handleLoseMediaDelete = () => {
  // Blob URLの場合はメモリ解放
  if (result.value.loseUrl && result.value.loseUrl.startsWith('blob:')) {
    URL.revokeObjectURL(result.value.loseUrl)
  }
  store.clearLoseMedia()
}

/**
 * 当落メッセージの変更を監視してリアルタイムバリデーション
 */
watch(
  result,
  () => {
    store.validateStep4()
  },
  { deep: true }
)

return { __sfc: true,proxy, store, result, errors, getFileExtension, isSupportedImageExtension, isWinImageFile, isLoseImageFile, getWinMediaFileName, getLoseMediaFileName, handleWinMediaSelectClick, handleLoseMediaSelectClick, handleMediaFileChange, handleWinMediaFileChange, handleLoseMediaFileChange, handleWinMediaDelete, handleLoseMediaDelete }
}

})